import type { LinkIntelligence, BacklinkProfile } from "@/types/analysis";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ExternalLink, Link, ArrowUpRight, Target, Lightbulb, CheckCircle2, AlertTriangle } from "lucide-react";
import { useState } from "react";

const competitionColors: Record<string, string> = {
  "low": "bg-success/15 text-success border-success/30",
  "medium": "bg-warning/15 text-warning border-warning/30",
  "high": "bg-destructive/15 text-destructive border-destructive/30",
  "very-high": "bg-destructive/20 text-destructive border-destructive/40",
};

const impactColors: Record<string, string> = {
  high: "bg-success/15 text-success border-success/30",
  medium: "bg-warning/15 text-warning border-warning/30",
  low: "bg-muted text-muted-foreground border-border",
};

function ImprovementItem({ item }: { item: LinkIntelligence["improvements"][0] }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border border-border rounded-lg overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-start justify-between gap-3 p-4 text-left hover:bg-muted/40 transition-colors">
            <div className="flex items-start gap-3 min-w-0">
              <Lightbulb className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={`text-xs border ${impactColors[item.impact]} capitalize`} variant="outline">
                {item.impact} impact
              </Badge>
              {item.example && (
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
              )}
            </div>
          </button>
        </CollapsibleTrigger>
        {item.example && (
          <CollapsibleContent>
            <div className="px-4 pb-4 border-t border-border bg-muted/20">
              <p className="text-xs text-muted-foreground mt-3 mb-1.5 font-medium uppercase tracking-wide">Example</p>
              <pre className="text-xs text-foreground/80 bg-background border border-border rounded p-3 whitespace-pre-wrap font-mono leading-relaxed">
                {item.example}
              </pre>
            </div>
          </CollapsibleContent>
        )}
      </div>
    </Collapsible>
  );
}

function LinkIntelligenceView({ data }: { data: LinkIntelligence }) {
  const internalPct = Math.round(data.internalLinkRatio * 100);
  const externalPct = 100 - internalPct;
  const progressPct = Math.min(
    100,
    data.estimatedReferringDomainsForPage1 > 0
      ? Math.round((data.currentEstimatedReferringDomains / data.estimatedReferringDomainsForPage1) * 100)
      : 0
  );

  return (
    <div className="space-y-4 mt-4">
      {/* Section A: Real crawl data */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Link className="w-4 h-4 text-primary" /> Page Link Breakdown
          <Badge variant="outline" className="text-xs border-success/30 text-success bg-success/10 ml-1">Real data</Badge>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Total Links Found", value: data.totalLinksFound.toLocaleString() },
            { label: "Internal Links", value: data.internalLinks.toLocaleString() },
            { label: "External Links", value: data.externalLinks.toLocaleString() },
            { label: "Unique External Domains", value: data.uniqueExternalDomains.toLocaleString() },
          ].map((s) => (
            <Card key={s.label} className="border-border bg-card">
              <CardContent className="p-4">
                <div className="text-xs text-muted-foreground mb-1">{s.label}</div>
                <div className="text-2xl font-bold text-foreground">{s.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Internal vs External ratio bar */}
        <Card className="border-border bg-card mt-3">
          <CardContent className="p-4">
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span>Internal ({internalPct}%)</span>
              <span>External ({externalPct}%)</span>
            </div>
            <div className="h-3 rounded-full bg-muted overflow-hidden flex">
              <div
                className="h-full bg-primary rounded-l-full transition-all"
                style={{ width: `${internalPct}%` }}
              />
              <div
                className="h-full bg-accent rounded-r-full transition-all"
                style={{ width: `${externalPct}%` }}
              />
            </div>
            {data.topExternalDomains.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-1.5">Top external domains linked from this page:</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.topExternalDomains.map((d) => (
                    <span key={d} className="text-xs bg-muted border border-border rounded px-2 py-0.5 flex items-center gap-1">
                      <ExternalLink className="w-2.5 h-2.5" />{d}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Internal Links table */}
        {data.topInternalLinks && data.topInternalLinks.length > 0 && (
          <Card className="border-border bg-card mt-3">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-foreground">Top Internal Links & Anchor Text</p>
                {data.internalLinks > 10 && (
                  <span className="text-xs text-muted-foreground">Showing top 10 of {data.internalLinks}</span>
                )}
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs h-8 py-1">Page</TableHead>
                      <TableHead className="text-xs h-8 py-1">Current Anchor Text</TableHead>
                      <TableHead className="text-xs h-8 py-1">Suggested Improvement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.topInternalLinks.map((link, i) => {
                      let path = link.url;
                      try { path = new URL(link.url).pathname || '/'; } catch { /* keep original */ }
                      return (
                        <TableRow key={i} className="text-xs">
                          <TableCell className="py-2 font-mono text-muted-foreground max-w-[140px] truncate" title={path}>
                            {path}
                          </TableCell>
                          <TableCell className="py-2">
                            <div className="flex items-center gap-1.5">
                              {link.needsImprovement
                                ? <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0" />
                                : <CheckCircle2 className="w-3.5 h-3.5 text-success shrink-0" />
                              }
                              <span className="text-foreground">{link.anchorText}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            {link.suggestedAnchorText ? (
                              <Badge variant="outline" className="text-xs bg-primary/8 text-primary border-primary/30 font-normal">
                                {link.suggestedAnchorText}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Section B: Authority benchmark */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-primary" /> Page 1 Authority Benchmark
          <Badge variant="outline" className="text-xs ml-1">AI estimate</Badge>
        </h3>
        <Card className="border-border bg-card">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Competition Level</p>
                <Badge className={`mt-1 border capitalize ${competitionColors[data.competitionLevel]}`} variant="outline">
                  {data.competitionLevel.replace("-", " ")}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Referring Domains Needed</p>
                <p className="text-2xl font-bold text-foreground">{data.estimatedReferringDomainsForPage1.toLocaleString()}</p>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                <span>Current estimated: ~{data.currentEstimatedReferringDomains}</span>
                <span>Target for Page 1: ~{data.estimatedReferringDomainsForPage1}</span>
              </div>
              <Progress value={progressPct} className="h-2.5" />
              <p className="text-xs text-muted-foreground mt-1">{progressPct}% of the way to estimated Page 1 authority</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-medium text-foreground mb-1">Internal Linking Quality</p>
                <div className="flex items-center gap-2">
                  <Progress value={data.internalLinkingScore} className="h-1.5 flex-1" />
                  <span className="text-xs text-muted-foreground shrink-0">{data.internalLinkingScore}/100</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">{data.internalLinkingAssessment}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-foreground mb-1">External Link Quality</p>
                {data.externalLinkQualityScore != null ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Progress value={data.externalLinkQualityScore} className="h-1.5 flex-1" />
                      <span className="text-xs text-muted-foreground shrink-0">{data.externalLinkQualityScore}/100</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">{data.externalLinkQuality}</p>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">{data.externalLinkQuality}</p>
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground border-t border-border pt-3 italic">
              {data.disclaimer}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Section C: Improvements */}
      {data.improvements.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <ArrowUpRight className="w-4 h-4 text-primary" /> Link Improvement Recommendations
          </h3>
          <div className="space-y-2">
            {data.improvements.map((item, i) => (
              <ImprovementItem key={i} item={item} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Legacy view for old reports that only have backlinkProfile
function LegacyBacklinkView({ data }: { data: BacklinkProfile }) {
  return (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Backlinks", value: data.totalBacklinks.toLocaleString() },
          { label: "Referring Domains", value: data.referringDomains.toLocaleString() },
          { label: "Do-Follow Ratio", value: `${Math.round(data.doFollowRatio * 100)}%` },
          { label: "Spam Risk", value: data.spamRisk, badge: true },
        ].map((s) => (
          <Card key={s.label} className="border-border bg-card">
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground mb-1">{s.label}</div>
              {s.badge ? (
                <Badge className={`${s.value === "low" ? "bg-success/20 text-success" : s.value === "medium" ? "bg-warning/20 text-warning" : "bg-destructive/20 text-destructive"} border-0`}>
                  {s.value}
                </Badge>
              ) : (
                <div className="text-2xl font-bold text-foreground">{s.value}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border-warning/30 bg-warning/5">
        <CardContent className="p-4">
          <p className="text-xs text-warning">This report was generated with an older version. Re-run the analysis to see the new Link Intelligence view.</p>
        </CardContent>
      </Card>
    </div>
  );
}

export const BacklinkTab = ({ data, linkIntelligence }: { data: BacklinkProfile; linkIntelligence?: LinkIntelligence }) => {
  if (linkIntelligence) {
    return <LinkIntelligenceView data={linkIntelligence} />;
  }
  return <LegacyBacklinkView data={data} />;
};
